import {
  withWorkroomInputCompatibility,
  withWorkroomReadCompatibility,
} from "./workroomCompatibility.ts";
import {
  MessageQueueReference,
  ThreadMessageQueuePauseCommand,
  ThreadMessageQueueResumeCommand,
  ThreadMessageQueueUpdateCommand,
} from "./messageQueue.ts";
import { ThreadUsageLimitShell, ThreadUsageLimitUpdateCommand } from "./usageLimit.ts";
import {
  MessageReactionState,
  MessageReactionContext,
  MessageReactionReply,
  MessageReactionEmoji,
} from "./messageReactions.ts";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import * as SchemaIssue from "effect/SchemaIssue";
import * as SchemaTransformation from "effect/SchemaTransformation";
import * as Struct from "effect/Struct";
import { ProviderOptionSelections } from "./model.ts";
import { RepositoryIdentity, ThreadEnvMode } from "./environment.ts";
import {
  ApprovalRequestId,
  CheckpointRef,
  CommandId,
  EventId,
  IsoDateTime,
  MessageId,
  NonNegativeInt,
  PositiveInt,
  ProjectId,
  ProviderItemId,
  ThreadId,
  TrimmedNonEmptyString,
  TrimmedString,
  TurnId,
} from "./baseSchemas.ts";
import {
  MessageContextResolution,
  AgentContextResolution,
  LocalContextResolution,
  HistoricalContextResolution,
} from "./contextResolution.ts";
import { ContextSelectionExpression, LocalContextSelection } from "./contextSelection.ts";
import { OfflineWorldCopyReference, OfflineContextReceipt } from "./offlineWorld.ts";
import { ProviderDriverKind, ProviderInstanceId } from "./providerInstance.ts";
import { HelperStatus, ThreadHelper } from "./helperThread.ts";
import { TurnProvenance } from "./providerRuntime.ts";

export const ORCHESTRATION_WS_METHODS = {
  dispatchCommand: "orchestration.dispatchCommand",
  getWorkflowScript: "orchestration.getWorkflowScript",
  getTurnDiff: "orchestration.getTurnDiff",
  getFullThreadDiff: "orchestration.getFullThreadDiff",
  searchThreads: "orchestration.searchThreads",
  getArchivedShellSnapshot: "orchestration.getArchivedShellSnapshot",
  getBringBackReadiness: "orchestration.getBringBackReadiness",
  subscribeShell: "orchestration.subscribeShell",
  subscribeThread: "orchestration.subscribeThread",
} as const;

export const ProviderApprovalPolicy = Schema.Literals([
  "untrusted",
  "on-failure",
  "on-request",
  "never",
]);
export type ProviderApprovalPolicy = typeof ProviderApprovalPolicy.Type;
export const ProviderSandboxMode = Schema.Literals([
  "read-only",
  "workspace-write",
  "danger-full-access",
]);
export type ProviderSandboxMode = typeof ProviderSandboxMode.Type;

/**
 * `ModelSelection` — selection of a model on a configured provider instance.
 *
 * The routing key is `instanceId` (a user-defined slug identifying one
 * configured provider instance). Drivers, credentials, working-directory
 * bindings, and any other per-instance state are recovered from the
 * runtime registry via the instance id.
 *
 * Wire legacy: persisted selections produced before the driver/instance
 * split carried a `provider: <driver-id>` field instead. The schema absorbs
 * that shape via a pre-decoding transform — `{provider, model}` is promoted
 * to `{instanceId: defaultInstanceIdForDriver(provider), model}`. No
 * post-decode compatibility code lives in the runtime; the transform is the
 * only compat surface.
 */
const ModelSelectionWire = Schema.Struct({
  instanceId: ProviderInstanceId,
  model: TrimmedNonEmptyString,
  options: Schema.optionalKey(ProviderOptionSelections),
});

// Source shape for persisted legacy payloads. Fields are typed as
// `Schema.Unknown` so malformed drafts still make it into the transform and
// fail validation through the target schema (with proper error messages)
// rather than at the source-struct layer where the error is less actionable.
const ModelSelectionSource = Schema.Struct({
  provider: Schema.optional(Schema.Unknown),
  instanceId: Schema.optional(Schema.Unknown),
  model: Schema.Unknown,
  options: Schema.optional(Schema.Unknown),
});

export const ModelSelection = ModelSelectionSource.pipe(
  Schema.decodeTo(
    ModelSelectionWire,
    SchemaTransformation.transformOrFail({
      decode: (raw) => {
        // Resolve the routing key: prefer an explicit `instanceId`; fall
        // back to promoting the legacy `provider` slug (the canonical
        // `defaultInstanceIdForDriver` mapping) so persisted rollout-era
        // payloads decode without data loss. The target schema brands the
        // string as `ProviderInstanceId`.
        const instanceIdSource =
          raw.instanceId !== undefined
            ? raw.instanceId
            : typeof raw.provider === "string"
              ? raw.provider
              : undefined;
        const base: Record<string, unknown> = {
          instanceId: instanceIdSource,
          model: raw.model,
        };
        if (raw.options !== undefined) base.options = raw.options;
        return Effect.succeed(base as typeof ModelSelectionWire.Encoded);
      },
      encode: (value) => {
        const base: Record<string, unknown> = {
          model: value.model,
          instanceId: value.instanceId,
        };
        if (value.options !== undefined) base.options = value.options;
        return Effect.succeed(base as typeof ModelSelectionSource.Encoded);
      },
    }),
  ),
);
export type ModelSelection = typeof ModelSelection.Type;

export const RuntimeMode = Schema.Literals([
  "approval-required",
  "auto-accept-edits",
  "auto",
  "full-access",
]);
export type RuntimeMode = typeof RuntimeMode.Type;
export const DEFAULT_RUNTIME_MODE: RuntimeMode = "full-access";
export const ProviderInteractionMode = Schema.Literals(["default", "plan"]);
export type ProviderInteractionMode = typeof ProviderInteractionMode.Type;
export const DEFAULT_PROVIDER_INTERACTION_MODE: ProviderInteractionMode = "default";
export const ProviderRequestKind = Schema.Literals([
  "command",
  "file-read",
  "file-change",
  "permissions",
]);
export type ProviderRequestKind = typeof ProviderRequestKind.Type;
export const ProviderApprovalDecision = Schema.Literals([
  "accept",
  "acceptForSession",
  "decline",
  "cancel",
]);
export type ProviderApprovalDecision = typeof ProviderApprovalDecision.Type;
export const ProviderUserInputAnswers = Schema.Record(Schema.String, Schema.Unknown);
export type ProviderUserInputAnswers = typeof ProviderUserInputAnswers.Type;

export const PROVIDER_SEND_TURN_MAX_INPUT_CHARS = 120_000;
/** The recorded handoff of a carried conversation (Can 46) a Send may carry; the renderer's own bound (`HANDOFF_MAX_BYTES`) is the same number. */
export const PROVIDER_HANDOFF_MAX_TEXT_CHARS = 256 * 1024;
export const PROVIDER_SEND_TURN_MAX_ATTACHMENTS = 8;
export const PROVIDER_SEND_TURN_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const PROVIDER_SEND_TURN_MAX_IMAGE_DATA_URL_CHARS = 14_000_000;
const CHAT_ATTACHMENT_ID_MAX_CHARS = 128;
// Correlation id is command id by design in this model.
export const CorrelationId = CommandId;
export type CorrelationId = typeof CorrelationId.Type;

const ChatAttachmentId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(CHAT_ATTACHMENT_ID_MAX_CHARS),
  Schema.isPattern(/^[a-z0-9_-]+$/i),
);
export type ChatAttachmentId = typeof ChatAttachmentId.Type;

export const ChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  id: ChatAttachmentId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
  // Retained selected-source attachments verify exact bytes on every delivery.
  contentSha256: Schema.optional(Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/))),
});
export type ChatImageAttachment = typeof ChatImageAttachment.Type;

const UploadChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
  dataUrl: TrimmedNonEmptyString.check(
    Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_IMAGE_DATA_URL_CHARS),
  ),
});
export type UploadChatImageAttachment = typeof UploadChatImageAttachment.Type;

/**
 * A stored non-image attachment as it rides on a message (UP8). The same
 * shape as assets.ts's ChatFileAttachment, which the upload door returns:
 * assets.ts imports this module for ProjectFaviconPath, so the member is
 * declared here rather than imported, keeping the module graph acyclic.
 * The store already holds the bytes; a message carries the reference only.
 */
export const ChatStoredFileAttachment = Schema.Struct({
  type: Schema.Literal("file"),
  id: TrimmedNonEmptyString.check(Schema.isMaxLength(128), Schema.isPattern(/^[a-z0-9_-]+$/i)),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(
    Schema.isMaxLength(100),
    Schema.isPattern(/^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/i),
  ),
  sizeBytes: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 50 * 1024 * 1024 })),
  contentSha256: Schema.optional(Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/))),
  origin: Schema.optional(Schema.Literals(["picker", "paste", "folded-paste", "drop"])),
});
export type ChatStoredFileAttachment = typeof ChatStoredFileAttachment.Type;

export const ChatAttachment = Schema.Union([ChatImageAttachment, ChatStoredFileAttachment]);
export type ChatAttachment = typeof ChatAttachment.Type;
// A stored file is already uploaded through the attachment door; a Send
// carries its reference, never its bytes.
const UploadChatAttachment = Schema.Union([UploadChatImageAttachment, ChatStoredFileAttachment]);
export type UploadChatAttachment = typeof UploadChatAttachment.Type;

export const ProjectScriptIcon = Schema.Literals([
  "play",
  "test",
  "lint",
  "configure",
  "build",
  "debug",
]);
export type ProjectScriptIcon = typeof ProjectScriptIcon.Type;

export const ProjectScript = Schema.Struct({
  id: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  command: TrimmedNonEmptyString,
  icon: ProjectScriptIcon,
  runOnWorktreeCreate: Schema.Boolean,
  /**
   * URL to open in the in-app browser preview when this script runs (or
   * when the user explicitly requests a preview). Optional; only honored on
   * the desktop build.
   */
  previewUrl: Schema.optional(TrimmedNonEmptyString),
  /**
   * When true, automatically open the preview panel pointed at `previewUrl`
   * the moment this script starts. Ignored without `previewUrl` or on web.
   */
  autoOpenPreview: Schema.optional(Schema.Boolean),
});
export type ProjectScript = typeof ProjectScript.Type;

export const ProjectFaviconPath = TrimmedNonEmptyString.check(
  Schema.isMaxLength(1024),
  Schema.isPattern(/\.(?:avif|gif|ico|jpe?g|png|svg|webp)$/i),
);
export type ProjectFaviconPath = typeof ProjectFaviconPath.Type;

export const OrchestrationProject = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  // Per-project override for where new threads start. Null/absent means
  // "no override": clients fall back to cleo.json, then the global setting.
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  // Optional on the wire so cached snapshots from older servers still decode.
  faviconPath: Schema.optional(Schema.NullOr(ProjectFaviconPath)),
  scripts: Schema.Array(ProjectScript),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  deletedAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationProject = typeof OrchestrationProject.Type;

export const OrchestrationMessageRole = Schema.Literals(["user", "assistant", "system"]);
export type OrchestrationMessageRole = typeof OrchestrationMessageRole.Type;

export const OrchestrationMessage = Schema.Struct({
  id: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  reactions: Schema.optional(MessageReactionState),
  reactionContext: Schema.optional(Schema.Array(MessageReactionContext)),
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  // RL-CAN-07: a user message owns the immutable boundary frozen at Send.
  // Optional keeps pre-resolution snapshots and non-user messages decodable.
  contextResolution: Schema.optional(MessageContextResolution),
  historicalContextResolution: Schema.optional(HistoricalContextResolution),
  turnId: Schema.NullOr(TurnId),
  // Optional so cached snapshots from servers predating persisted turn
  // provenance remain decodable. Exact legacy turn joins use `{ unknown }`.
  turnProvenance: Schema.optional(TurnProvenance),
  streaming: Schema.Boolean,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  // How many leading characters of `text` are connection prose written by the
  // Slack connection before 3 Oct 2026, not the person's words (migration 085
  // records it for messages that existed then; nothing written later has it).
  // The person's words are `text` minus this prefix (withoutConnectionPrefix).
  connectionPrefixLength: Schema.optional(NonNegativeInt),
});
export type OrchestrationMessage = typeof OrchestrationMessage.Type;

export const OrchestrationProposedPlanId = TrimmedNonEmptyString;
export type OrchestrationProposedPlanId = typeof OrchestrationProposedPlanId.Type;

export const OrchestrationProposedPlan = Schema.Struct({
  id: OrchestrationProposedPlanId,
  turnId: Schema.NullOr(TurnId),
  planMarkdown: TrimmedNonEmptyString,
  implementedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  implementationThreadId: Schema.NullOr(ThreadId).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProposedPlan = typeof OrchestrationProposedPlan.Type;

const SourceProposedPlanReference = Schema.Struct({
  threadId: ThreadId,
  planId: OrchestrationProposedPlanId,
});

export const OrchestrationSessionStatus = Schema.Literals([
  "idle",
  "starting",
  "running",
  "ready",
  "interrupted",
  "stopped",
  "error",
]);
export type OrchestrationSessionStatus = typeof OrchestrationSessionStatus.Type;

/**
 * Why a session's last error happened, when it is a notice the person acts on
 * rather than a failure (W-0b60a0cc). A closed set:
 * - session-folder-missing: the Send was refused before it reached the engine
 *   because the conversation's folder is gone from this Mac.
 */
export const OrchestrationSessionErrorReason = Schema.Literals(["session-folder-missing"]);
export type OrchestrationSessionErrorReason = typeof OrchestrationSessionErrorReason.Type;

export const OrchestrationSession = Schema.Struct({
  threadId: ThreadId,
  status: OrchestrationSessionStatus,
  providerName: Schema.NullOr(TrimmedNonEmptyString),
  providerInstanceId: Schema.optional(ProviderInstanceId),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  activeTurnId: Schema.NullOr(TurnId),
  lastError: Schema.NullOr(TrimmedNonEmptyString),
  /** Set only with status "error" and a lastError it explains. */
  lastErrorReason: Schema.optional(OrchestrationSessionErrorReason),
  updatedAt: IsoDateTime,
});
export type OrchestrationSession = typeof OrchestrationSession.Type;

/**
 * Whether a session's error is a failure the person should see as trouble
 * (Failed, "in trouble", the header's error, a failed notification). A session
 * in "error" for a reason that is a notice (W-0b60a0cc: a missing folder refused
 * the Send before the engine saw it) is not: its status stays "error" for every
 * reader that asks whether the start failed, and its lastError still says what
 * to do, but nothing in the conversation broke.
 */
export function sessionInTrouble(
  session:
    | {
        readonly status: string;
        readonly lastErrorReason?: OrchestrationSessionErrorReason | null | undefined;
      }
    | null
    | undefined,
): boolean {
  return session?.status === "error" && session.lastErrorReason == null;
}

export const OrchestrationCheckpointFile = Schema.Struct({
  path: TrimmedNonEmptyString,
  kind: TrimmedNonEmptyString,
  additions: NonNegativeInt,
  deletions: NonNegativeInt,
});
export type OrchestrationCheckpointFile = typeof OrchestrationCheckpointFile.Type;

export const OrchestrationCheckpointStatus = Schema.Literals(["ready", "missing", "error"]);
export type OrchestrationCheckpointStatus = typeof OrchestrationCheckpointStatus.Type;

export const OrchestrationCheckpointSummary = Schema.Struct({
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});
export type OrchestrationCheckpointSummary = typeof OrchestrationCheckpointSummary.Type;

export const OrchestrationThreadActivityTone = Schema.Literals([
  "info",
  "tool",
  "approval",
  "error",
]);
export type OrchestrationThreadActivityTone = typeof OrchestrationThreadActivityTone.Type;

export const OrchestrationThreadActivity = Schema.Struct({
  id: EventId,
  tone: OrchestrationThreadActivityTone,
  kind: TrimmedNonEmptyString,
  summary: TrimmedNonEmptyString,
  payload: Schema.Unknown,
  turnId: Schema.NullOr(TurnId),
  sequence: Schema.optional(NonNegativeInt),
  createdAt: IsoDateTime,
});
export type OrchestrationThreadActivity = typeof OrchestrationThreadActivity.Type;

const OrchestrationLatestTurnState = Schema.Literals([
  "running",
  "interrupted",
  "completed",
  "error",
]);
export type OrchestrationLatestTurnState = typeof OrchestrationLatestTurnState.Type;

export const OrchestrationLatestTurn = Schema.Struct({
  turnId: TurnId,
  // Exact committed input; absent on historical snapshots without this link.
  userMessageId: Schema.optional(Schema.NullOr(MessageId)),
  state: OrchestrationLatestTurnState,
  requestedAt: IsoDateTime,
  startedAt: Schema.NullOr(IsoDateTime),
  completedAt: Schema.NullOr(IsoDateTime),
  assistantMessageId: Schema.NullOr(MessageId),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
});
export type OrchestrationLatestTurn = typeof OrchestrationLatestTurn.Type;

export const ThreadTitleRegeneration = Schema.Struct({
  requestId: CommandId,
  startedAt: IsoDateTime,
});
export type ThreadTitleRegeneration = typeof ThreadTitleRegeneration.Type;

// ─── Fork (Can 46) ─────────────────────────────────────────────────────────
// A fork carries what was said up to one message of its source as its own
// immutable record: each entry is a snapshot (text, times, provenance and
// attachment names copied at fork birth), never a reference, so a later edit
// or deletion of the source cannot change the fork. Entries are not message
// rows: they have no turn and are never read as work in flight.

/** Entries one `thread.history-inherited` event carries, at most. */
export const FORK_INHERITED_EVENT_MAX_ENTRIES = 100;
/** UTF-8 bytes of entry text one `thread.history-inherited` event carries, at most (one longer entry still travels alone). */
export const FORK_INHERITED_EVENT_MAX_TEXT_BYTES = 256 * 1024;
/** Entries a fork inherits, at most; older entries are counted in `inheritedOmitted`. */
export const FORK_INHERITED_MAX_ENTRIES = 2_000;
/** UTF-8 bytes of entry text a fork inherits, at most; older entries are counted in `inheritedOmitted`. */
export const FORK_INHERITED_MAX_TEXT_BYTES = 4 * 1024 * 1024;

/** Attachments whose bytes a fork copies at birth, at most (the newest); older ones stay names only. */
export const FORK_INHERITED_MAX_ATTACHMENT_COPIES = 32;
/** Bytes of attachment files a fork copies at birth, at most; older ones stay names only. */
export const FORK_INHERITED_MAX_ATTACHMENT_COPY_BYTES = 256 * 1024 * 1024;
/** Steps kept per inherited Cleo reply, at most (the handoff lists fewer). */
export const FORK_INHERITED_MAX_STEPS_PER_ENTRY = 200;
/** JSON bytes of recorded steps a fork keeps in all, newest replies first. */
export const FORK_INHERITED_MAX_DONE_BYTES = 256 * 1024;

export const InheritedAttachmentReference = Schema.Struct({
  type: Schema.Literals(["image", "file"]),
  name: TrimmedNonEmptyString,
  // The source message's attachment id. The source keeps its own file.
  sourceAttachmentId: TrimmedNonEmptyString,
  // The fork's own copy of the file (I9), stored under the fork's thread:
  // editing or removing the source's attachment cannot change it. Absent when
  // the bytes were not copied (a fork made before copies, past the copy bound,
  // or a file that could not be read at birth); then only the name travels.
  copy: Schema.optional(ChatAttachment),
});
export type InheritedAttachmentReference = typeof InheritedAttachmentReference.Type;

/**
 * One step Cleo took while writing a reply, as a fork keeps it (Can 21's "done"
 * ledger, carried into the fork): what was done and how it turned out, never an
 * output or a file's contents. The same shape the handoff renders.
 */
export const InheritedDoneStep = Schema.Struct({
  kind: Schema.Literals([
    "command",
    "file-read",
    "file-change",
    "tool",
    "web-search",
    "approval",
    "denied",
  ]),
  title: Schema.String,
  detail: Schema.String,
  outcome: Schema.Literals(["ok", "failed", "denied", "unknown"]),
  files: Schema.optional(
    Schema.Array(
      Schema.Struct({
        path: Schema.String,
        additions: NonNegativeInt,
        deletions: NonNegativeInt,
      }),
    ),
  ),
  approval: Schema.optional(Schema.Literal("asked")),
});
export type InheritedDoneStep = typeof InheritedDoneStep.Type;

export const InheritedMessage = Schema.Struct({
  ordinal: NonNegativeInt,
  role: Schema.Literals(["person", "cleo"]),
  text: Schema.String,
  // Previously delivered fork findings travel with the message without
  // changing the person's visible text in a later fork.
  carriedContext: Schema.optional(Schema.String),
  createdAt: IsoDateTime,
  sourceThreadId: ThreadId,
  sourceMessageId: MessageId,
  // The source timeline's turn provenance for one of Cleo's replies; null for
  // the person's messages.
  provenance: Schema.NullOr(TurnProvenance),
  attachments: Schema.Array(InheritedAttachmentReference),
  interrupted: Schema.Boolean,
  // The connection prefix recorded for the source message this entry copies
  // (see OrchestrationMessage.connectionPrefixLength), read through its id.
  connectionPrefixLength: Schema.optional(NonNegativeInt),
  // The steps Cleo took for this reply in the source (listed on the last reply
  // of a turn), kept at birth so the fork's handoff can say what was done, not
  // only what was said. Absent: not kept (an older fork, or past the bound);
  // empty: there were none. Sent to clients: the fork's view
  // shows them as a collapsed line under the reply.
  done: Schema.optional(Schema.Array(InheritedDoneStep)),
});
export type InheritedMessage = typeof InheritedMessage.Type;

export const ThreadForkedFrom = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  sourceTitle: TrimmedNonEmptyString,
  inheritedCount: NonNegativeInt,
});
export type ThreadForkedFrom = typeof ThreadForkedFrom.Type;

/** The fork point a `thread.create` names. */
export const ThreadForkRequest = Schema.Struct({
  threadId: ThreadId,
  throughMessageId: MessageId,
});
export type ThreadForkRequest = typeof ThreadForkRequest.Type;

/**
 * A conversation the thread's engine has not yet been given: a fork's
 * inherited record (reason "fork"), or everything said before a Send that
 * moved the conversation to another engine (reason "engine-switch", Can 21).
 * Null for a thread that has neither. Only the server marks it delivered.
 */
export const ConversationCarry = Schema.Struct({
  state: Schema.Literals(["pending", "delivered"]),
  reason: Schema.Literals(["fork", "engine-switch"]),
  handoffId: Schema.NullOr(TrimmedNonEmptyString),
  /**
   * W-79778d2b: the person undid the conversation to an earlier message on an
   * engine that keeps every turn in its own session, so that engine starts
   * fresh with the kept messages. Its reason stays "engine-switch" (a fresh
   * session is given the conversation, as after a switch): an older client
   * knows only the two reasons and drops this field. Set only while the carry
   * is pending; the server stores no column for it (read from the event log).
   */
  afterUndo: Schema.optional(Schema.Literal(true)),
});
export type ConversationCarry = typeof ConversationCarry.Type;

// Immutable at session birth; descendants inherit the same restriction.
export const LocalWorkPolicy = Schema.Struct({
  execution: Schema.Literal("local-only"),
  disclosure: Schema.Literal("explicit"),
  offlineCopy: Schema.optional(OfflineWorldCopyReference),
});
export type LocalWorkPolicy = typeof LocalWorkPolicy.Type;

export const OrchestrationThread = withWorkroomReadCompatibility(
  Schema.Struct({
    id: ThreadId,
    projectId: ProjectId,
    // Historical threads replay as null; optional keeps pre-World snapshots decodable.
    worldKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    // Optional execution binding to an authoritative World session (Arc RM).
    // Historical World threads and Local harness threads decode as null;
    // neither becomes a synthetic session.
    sessionId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    title: TrimmedNonEmptyString,
    // Arc A5 explicit parentage: null for roots, the named parent for children.
    // Optional so pre-A5 snapshots decode.
    parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
    // The launching workroom's key (Arc RM); optional so earlier snapshots
    // decode, null for threads in a general room.
    workroomKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    localSelection: Schema.optional(LocalContextSelection),
    localWorkPolicy: Schema.optional(Schema.NullOr(LocalWorkPolicy)),
    modelSelection: ModelSelection,
    runtimeMode: RuntimeMode,
    interactionMode: ProviderInteractionMode.pipe(
      Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
    ),
    branch: Schema.NullOr(TrimmedNonEmptyString),
    worktreePath: Schema.NullOr(TrimmedNonEmptyString),
    latestTurn: Schema.NullOr(OrchestrationLatestTurn),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
    settledOverride: Schema.NullOr(Schema.Literals(["settled", "active"])).pipe(
      Schema.withDecodingDefault(Effect.succeed(null)),
    ),
    settledAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
    // Snooze is an overlay on the active lifecycle, not a fourth destination:
    // a snoozed thread stays "active" in the model and is only suppressed from
    // the inbox until snoozedUntil passes (or the thread raises its hand).
    // Optional so payloads from pre-snooze servers still decode.
    snoozedUntil: Schema.optional(Schema.NullOr(IsoDateTime)),
    snoozedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
    // A pin overrides the settled/snoozed lifecycle: while pinnedAt is set the
    // thread renders in the pinned block and never classifies into a shelf.
    // Optional so payloads from pre-pinning servers still decode.
    pinnedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
    // Fractional index for user-arranged pinned order. Keyed threads sort by
    // string comparison ahead of keyless ones (which keep creation order), so
    // servers never need each other's threads to agree on the merged list.
    // Optional so payloads from pre-reorder servers still decode.
    pinOrderKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    // Fractional index for the user-arranged order of the active (unpinned,
    // unsettled) block; same string-comparison contract as pinOrderKey. Cleared
    // when the thread settles. Optional so pre-arrangement servers still decode.
    activeOrderKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    // Pending-only state. Optional so older servers remain compatible.
    titleRegeneration: Schema.optional(Schema.NullOr(ThreadTitleRegeneration)),
    deletedAt: Schema.NullOr(IsoDateTime),
    historyRevision: Schema.optional(TrimmedNonEmptyString),
    // Fork (Can 46): where this conversation came from; absent or null for a
    // thread that is not a fork. Optional so earlier snapshots decode.
    forkedFrom: Schema.optional(Schema.NullOr(ThreadForkedFrom)),
    conversationCarry: Schema.optional(Schema.NullOr(ConversationCarry)),
    // Can 54: set on a conversation that is one helper's read-only record;
    // absent or null on every other thread. Optional so earlier snapshots decode.
    helper: Schema.optional(Schema.NullOr(ThreadHelper)),
    // Detail reads only: the fork's inherited record, oldest first, and how
    // many older entries the fork's bound left out. Never windowed.
    inheritedMessages: Schema.optional(Schema.Array(InheritedMessage)),
    inheritedOmitted: Schema.optional(NonNegativeInt),
    messages: Schema.Array(OrchestrationMessage),
    proposedPlans: Schema.Array(OrchestrationProposedPlan).pipe(
      Schema.withDecodingDefault(Effect.succeed([])),
    ),
    activities: Schema.Array(OrchestrationThreadActivity),
    checkpoints: Schema.Array(OrchestrationCheckpointSummary),
    session: Schema.NullOr(OrchestrationSession),
  }),
);
export type OrchestrationThread = typeof OrchestrationThread.Type;

export const OrchestrationReadModel = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProject),
  threads: Schema.Array(OrchestrationThread),
  updatedAt: IsoDateTime,
});
export type OrchestrationReadModel = typeof OrchestrationReadModel.Type;

export const OrchestrationProjectShell = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  // Optional on the wire so cached snapshots from older servers still decode.
  faviconPath: Schema.optional(Schema.NullOr(ProjectFaviconPath)),
  scripts: Schema.Array(ProjectScript),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProjectShell = typeof OrchestrationProjectShell.Type;

export const OrchestrationThreadShell = withWorkroomReadCompatibility(
  Schema.Struct({
    id: ThreadId,
    projectId: ProjectId,
    // Historical threads are null; optional keeps pre-World shell snapshots decodable.
    worldKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    sessionId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    title: TrimmedNonEmptyString,
    // Arc A5 explicit parentage: null for roots, the named parent for children.
    // Optional so pre-A5 servers/clients interop.
    parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
    // Fork (Can 46); optional for interop, null for a thread that is not a fork.
    forkedFrom: Schema.optional(Schema.NullOr(ThreadForkedFrom)),
    // A conversation its engine has not been given yet (fork or engine
    // switch); optional for interop, absent or null when there is none.
    conversationCarry: Schema.optional(Schema.NullOr(ConversationCarry)),
    // Can 54: see OrchestrationThread.helper.
    helper: Schema.optional(Schema.NullOr(ThreadHelper)),
    // What a conversation Cleo started for this one (dispatch_provider_targets,
    // create_child_session) was asked to do, carried on child shells only so
    // the conversation that started it can show the ask. Optional for interop.
    launchAsk: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    // The launching workroom's key (Arc RM); optional for interop, null for
    // threads in a general room.
    workroomKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    localWorkPolicy: Schema.optional(Schema.NullOr(LocalWorkPolicy)),
    modelSelection: ModelSelection,
    runtimeMode: RuntimeMode,
    interactionMode: ProviderInteractionMode.pipe(
      Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
    ),
    branch: Schema.NullOr(TrimmedNonEmptyString),
    worktreePath: Schema.NullOr(TrimmedNonEmptyString),
    latestTurn: Schema.NullOr(OrchestrationLatestTurn),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
    settledOverride: Schema.NullOr(Schema.Literals(["settled", "active"])).pipe(
      Schema.withDecodingDefault(Effect.succeed(null)),
    ),
    settledAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
    snoozedUntil: Schema.optional(Schema.NullOr(IsoDateTime)),
    snoozedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
    pinnedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
    pinOrderKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    activeOrderKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
    titleRegeneration: Schema.optional(Schema.NullOr(ThreadTitleRegeneration)),
    session: Schema.NullOr(OrchestrationSession),
    latestUserMessageAt: Schema.NullOr(IsoDateTime),
    /**
     * The Room Rail's hover preview (RM-CAN-10, 2026-09-15): the opening
     * characters of the latest user message, folded beside its time. Optional
     * so older servers/clients interop; absent or null = no text to show.
     */
    latestUserMessagePreview: Schema.optional(Schema.NullOr(Schema.String)),
    hasPendingApprovals: Schema.Boolean,
    hasPendingUserInput: Schema.Boolean,
    hasActionableProposedPlan: Schema.Boolean,
    /**
     * Native background work alive after the turn settles: "working" while
     * subagents/workflows run, "monitoring" when watch loops are the only
     * live work. Optional so old servers/clients interop; absent = none.
     */
    backgroundLiveness: Schema.optional(Schema.NullOr(Schema.Literals(["working", "monitoring"]))),
    /**
     * The conversation stopped on a usage limit that has not been resumed or
     * dismissed (Can 24): the sidebar shows "Limited" with the reset time.
     * Optional so old servers/clients interop; absent or null = not limited.
     */
    usageLimit: Schema.optional(Schema.NullOr(ThreadUsageLimitShell)),
    /**
     * Current plan step while a turn runs, for the Working indicators
     * (sidebar row, in-chat working line). Cleared when the turn settles —
     * never persists as stale UI. Optional so old servers/clients interop.
     */
    planProgress: Schema.optional(
      Schema.NullOr(
        Schema.Struct({
          step: TrimmedNonEmptyString,
          completedSteps: NonNegativeInt,
          totalSteps: NonNegativeInt,
        }),
      ),
    ),
  }),
);
export type OrchestrationThreadShell = typeof OrchestrationThreadShell.Type;

/** Characters of a user message the thread shell carries for the rail's hover preview. */
export const LATEST_USER_MESSAGE_PREVIEW_CHARS = 240;

/**
 * The shell's preview of one user message: whitespace collapsed, trimmed, cut
 * to LATEST_USER_MESSAGE_PREVIEW_CHARS code points. Null when nothing is left
 * to show (an attachment-only or empty message).
 */
export function latestUserMessagePreviewOf(text: string): string | null {
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed.length === 0) return null;
  const points = Array.from(collapsed);
  return points.length <= LATEST_USER_MESSAGE_PREVIEW_CHARS
    ? collapsed
    : points.slice(0, LATEST_USER_MESSAGE_PREVIEW_CHARS).join("");
}

export const OrchestrationShellSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProjectShell),
  threads: Schema.Array(OrchestrationThreadShell),
  updatedAt: IsoDateTime,
});
export type OrchestrationShellSnapshot = typeof OrchestrationShellSnapshot.Type;

export const OrchestrationShellStreamEvent = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("project-upserted"),
    sequence: NonNegativeInt,
    project: OrchestrationProjectShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("project-removed"),
    sequence: NonNegativeInt,
    projectId: ProjectId,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-upserted"),
    sequence: NonNegativeInt,
    thread: OrchestrationThreadShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-removed"),
    sequence: NonNegativeInt,
    threadId: ThreadId,
  }),
]);
export type OrchestrationShellStreamEvent = typeof OrchestrationShellStreamEvent.Type;

export const OrchestrationShellStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("synchronized"),
  }),
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: OrchestrationShellSnapshot,
  }),
  OrchestrationShellStreamEvent,
]);
export type OrchestrationShellStreamItem = typeof OrchestrationShellStreamItem.Type;

export const OrchestrationSubscribeShellInput = Schema.Struct({
  /**
   * When provided, the server skips the initial full shell snapshot and instead
   * replays shell events after this sequence before streaming live events.
   * Clients that already hold a cached (or HTTP-loaded) shell snapshot pass its
   * sequence here so the subscription resumes without re-sending the entire
   * projects/threads list (overlapping events are deduped by sequence on the
   * client).
   */
  afterSequence: Schema.optionalKey(NonNegativeInt),
  /**
   * Requests an explicit marker after the subscription has emitted its initial
   * snapshot or catch-up replay and before it begins emitting live events.
   */
  requestCompletionMarker: Schema.optionalKey(Schema.Boolean),
});
export type OrchestrationSubscribeShellInput = typeof OrchestrationSubscribeShellInput.Type;

export const OrchestrationSubscribeThreadInput = Schema.Struct({
  threadId: ThreadId,
  /**
   * When provided, the server skips the initial snapshot frame and instead
   * replays events after this sequence before streaming live events. Clients
   * that load the snapshot over HTTP pass the snapshot's sequence here so the
   * live subscription resumes without a gap (overlapping events are deduped by
   * sequence on the client).
   */
  afterSequence: Schema.optionalKey(NonNegativeInt),
  /**
   * Requests an explicit marker after the subscription has emitted its initial
   * snapshot or catch-up replay and before it begins emitting live events.
   */
  requestCompletionMarker: Schema.optionalKey(Schema.Boolean),
  /**
   * When provided, the fallback snapshot frame (sent when `afterSequence` is
   * missing or the catch-up gap is too large) is windowed to the last
   * `turnLimit` user-anchored turns and carries `page` metadata. Absent means
   * the fallback snapshot is the full thread, preserving pre-pagination client
   * behavior. Live events are unaffected either way.
   */
  turnLimit: Schema.optionalKey(PositiveInt),
});
export type OrchestrationSubscribeThreadInput = typeof OrchestrationSubscribeThreadInput.Type;

/**
 * Bounds a thread detail read to a window of recent turns. `turnLimit` counts
 * turns with a user pending message (subagent/fan-out turns between them ride
 * along), so the window always contains the last N user prompts. `beforeCursor`
 * requests the disjoint page of older turns strictly before a previously
 * returned cursor. Requests without a window get the full thread; pagination is
 * strictly opt-in so older clients keep today's behavior on both HTTP and the
 * WebSocket fallback snapshot.
 */
export const OrchestrationThreadDetailWindow = Schema.Struct({
  turnLimit: Schema.optionalKey(PositiveInt),
  beforeCursor: Schema.optionalKey(TrimmedNonEmptyString),
});
export type OrchestrationThreadDetailWindow = typeof OrchestrationThreadDetailWindow.Type;

/**
 * Page metadata for a windowed thread detail read. `beforeCursor` is opaque and
 * exclusive: passing it back returns the adjacent disjoint slice of older
 * turns. `null` means the thread is fully loaded below this page. The
 * `snapshotSequence` mirrors the top-level snapshot sequence so history pages
 * can be sequence-checked against live state before merging.
 */
export const OrchestrationThreadDetailPage = Schema.Struct({
  beforeCursor: Schema.NullOr(TrimmedNonEmptyString),
  hasMore: Schema.Boolean,
  snapshotSequence: NonNegativeInt,
  /**
   * Highest event sequence applied to THIS thread at page read time. The
   * global `snapshotSequence` advances with every thread's events, so a
   * client cannot wait for it via its per-thread subscription; this
   * thread-scoped watermark is reachable. A client merging an older page
   * must first have applied live events up to it — otherwise a streaming
   * turn outside the loaded window could have deltas replayed on top of
   * page content that already includes them, duplicating text.
   */
  threadSequence: Schema.optionalKey(NonNegativeInt),
});
export type OrchestrationThreadDetailPage = typeof OrchestrationThreadDetailPage.Type;

export const OrchestrationThreadDetailSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  thread: OrchestrationThread,
  // Present only on windowed responses. Absent on full snapshots (and from
  // pre-pagination servers), which clients treat as fully loaded.
  page: Schema.optional(OrchestrationThreadDetailPage),
});
export type OrchestrationThreadDetailSnapshot = typeof OrchestrationThreadDetailSnapshot.Type;

export const ProjectCreateCommand = Schema.Struct({
  type: Schema.Literal("project.create"),
  commandId: CommandId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  createWorkspaceRootIfMissing: Schema.optional(Schema.Boolean),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  createdAt: IsoDateTime,
});

const ProjectMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("project.meta.update"),
  commandId: CommandId,
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  // Absent = leave unchanged; null = clear the override.
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  faviconPath: Schema.optional(Schema.NullOr(ProjectFaviconPath)),
  scripts: Schema.optional(Schema.Array(ProjectScript)),
});

const ProjectDeleteCommand = Schema.Struct({
  type: Schema.Literal("project.delete"),
  commandId: CommandId,
  projectId: ProjectId,
  force: Schema.optional(Schema.Boolean),
});

const ThreadCreateCommand = withWorkroomInputCompatibility(
  Schema.Struct({
    type: Schema.Literal("thread.create"),
    commandId: CommandId,
    threadId: ThreadId,
    projectId: ProjectId,
    worldKey: Schema.optional(TrimmedNonEmptyString),
    // The persisted World session this local execution belongs to (Arc RM). A
    // World command that omits it names the World's standing room's first
    // session, S_harmony_session; a Local command omits both World and session.
    sessionId: Schema.optional(TrimmedNonEmptyString),
    title: TrimmedNonEmptyString,
    // Arc A3 launch intent: durable and independent of the display title, which
    // first-turn generation replaces. Optional so existing callers decode.
    launchAsk: Schema.optional(TrimmedNonEmptyString),
    // Arc A5 explicit parentage: present only for an explicit child session or
    // branch; absent means the thread is a root.
    parentThreadId: Schema.optional(ThreadId),
    // Local continuation copies the exact selection already prepared for Send.
    contextFromMessageId: Schema.optional(MessageId),
    // Fork (Can 46): start from everything said in the named thread up to and
    // including the named message. The source is left unchanged.
    forkedFrom: Schema.optional(ThreadForkRequest),
    // The launching workroom's key (Arc RM) — the board kind the room opens
    // with, resolved against the World authority's board registry; absent means
    // a thread in a general room.
    workroomKey: Schema.optional(TrimmedNonEmptyString),
    localWorkPolicy: Schema.optional(LocalWorkPolicy),
    modelSelection: ModelSelection,
    runtimeMode: RuntimeMode,
    interactionMode: ProviderInteractionMode.pipe(
      Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
    ),
    branch: Schema.NullOr(TrimmedNonEmptyString),
    worktreePath: Schema.NullOr(TrimmedNonEmptyString),
    createdAt: IsoDateTime,
  }),
);

const ThreadDeleteCommand = Schema.Struct({
  type: Schema.Literal("thread.delete"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadArchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.archive"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadUnarchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.unarchive"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadSettleCommand = Schema.Struct({
  type: Schema.Literal("thread.settle"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadUnsettleCommand = Schema.Struct({
  type: Schema.Literal("thread.unsettle"),
  commandId: CommandId,
  threadId: ThreadId,
  // Commands only carry "user": activity un-settles are decided server-side
  // (the decider emits thread.unsettled(reason: "activity") events directly,
  // never through this command), so a client cannot forge the neutral reset.
  reason: Schema.Literal("user"),
});

const ThreadSnoozeCommand = Schema.Struct({
  type: Schema.Literal("thread.snooze"),
  commandId: CommandId,
  threadId: ThreadId,
  // The wake time. Event-based wake conditions (PR merged, review posted)
  // will arrive as an optional condition field alongside this; time-based
  // snooze is just the first kind of condition.
  snoozedUntil: IsoDateTime,
});

const ThreadUnsnoozeCommand = Schema.Struct({
  type: Schema.Literal("thread.unsnooze"),
  commandId: CommandId,
  threadId: ThreadId,
  // Commands only carry "user": activity wakes are decided server-side (the
  // decider emits thread.unsnoozed(reason: "activity") directly), and timer
  // wakes need no event at all — clients derive visibility from snoozedUntil,
  // so a passed wake time simply stops classifying as snoozed.
  reason: Schema.Literal("user"),
});

const ThreadPinCommand = Schema.Struct({
  type: Schema.Literal("thread.pin"),
  commandId: CommandId,
  threadId: ThreadId,
  // Initial slot in the user-arranged pinned order (see ThreadPinReorderCommand).
  // Optional: clients on pre-reorder servers omit it, and the pinned block
  // falls back to creation order for keyless threads.
  orderKey: Schema.optional(TrimmedNonEmptyString),
});

const ThreadUnpinCommand = Schema.Struct({
  type: Schema.Literal("thread.unpin"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadPinReorderCommand = Schema.Struct({
  type: Schema.Literal("thread.pin.reorder"),
  commandId: CommandId,
  threadId: ThreadId,
  // Fractional index key: pinned threads sort by plain string comparison of
  // these keys, so a drag writes one key to one thread — neighbors (possibly
  // on other servers) are never touched. Clients compute a key that sorts
  // between the dropped position's neighbors.
  orderKey: TrimmedNonEmptyString,
});

// Arranging the active block is not activity: the thread keeps its updatedAt
// and its slot rides the existing thread.meta-updated event, so clients on
// older servers decode it as a plain metadata update.
const ThreadActiveReorderCommand = Schema.Struct({
  type: Schema.Literal("thread.active.reorder"),
  commandId: CommandId,
  threadId: ThreadId,
  orderKey: TrimmedNonEmptyString,
});

const ThreadMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.meta.update"),
  commandId: CommandId,
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  regenerateTitle: Schema.optional(Schema.Literal(true)),
  modelSelection: Schema.optional(ModelSelection),
  branch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  expectedBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
}).check(
  Schema.makeFilter(
    (input) =>
      !(input.title !== undefined && input.regenerateTitle === true) ||
      "title and regenerateTitle cannot be specified together",
  ),
);

const ThreadContextPrepareCommand = Schema.Struct({
  type: Schema.Literal("thread.context.prepare"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  expectedCut: Schema.optional(NonNegativeInt),
  contextResolution: Schema.optional(LocalContextResolution),
  createdAt: IsoDateTime,
});

const ThreadContextMoveCommand = Schema.Struct({
  type: Schema.Literal("thread.context.move"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedCut: NonNegativeInt,
  codeRoot: Schema.optional(TrimmedNonEmptyString),
  restoreFromResolutionId: Schema.optional(TrimmedNonEmptyString),
  expressions: Schema.Array(ContextSelectionExpression),
  tools: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  replace: Schema.optional(Schema.Boolean),
  clearGroups: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  createdAt: IsoDateTime,
});

const ThreadRuntimeModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.runtime-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  runtimeMode: RuntimeMode,
  createdAt: IsoDateTime,
});

const ThreadInteractionModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.interaction-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  interactionMode: ProviderInteractionMode,
  createdAt: IsoDateTime,
});

const ThreadTurnStartBootstrapCreateThread = Schema.Struct({
  projectId: ProjectId,
  worldKey: Schema.optional(TrimmedNonEmptyString),
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});

const ThreadTurnStartBootstrapPrepareWorktree = Schema.Struct({
  projectCwd: TrimmedNonEmptyString,
  baseBranch: TrimmedNonEmptyString,
  branch: Schema.optional(TrimmedNonEmptyString),
  startFromOrigin: Schema.optional(Schema.Boolean),
});

const ThreadTurnStartBootstrap = Schema.Struct({
  createThread: Schema.optional(ThreadTurnStartBootstrapCreateThread),
  prepareWorktree: Schema.optional(ThreadTurnStartBootstrapPrepareWorktree),
  runSetupScript: Schema.optional(Schema.Boolean),
});

export type ThreadTurnStartBootstrap = typeof ThreadTurnStartBootstrap.Type;

/**
 * How a Send that arrives while a response is running is delivered:
 * `after-response` waits as Unread until the running turn ends; `now` joins
 * the running turn (a Steer). With nothing running, both are an ordinary Send.
 */
export const ThreadTurnQueueDelivery = Schema.Literals(["after-response", "now"]);
export type ThreadTurnQueueDelivery = typeof ThreadTurnQueueDelivery.Type;

/**
 * Can 46 (I10): a Send in the original conversation that brings back what
 * one of its forks found since it split. The decider accepts only a fork of
 * this thread that is idle, in the same World or both Local; the reactor
 * gives this Send's engine the fork's own record as a merge-back handoff.
 */
export const ThreadBringBack = Schema.Struct({
  forkThreadId: ThreadId,
});
export type ThreadBringBack = typeof ThreadBringBack.Type;

/**
 * Can 46 (W-d0329709): what a bring-back says when everything the fork said
 * was already brought back. The original's own record of delivered bring-backs
 * decides it (a Send whose turn completed), never a note kept on the fork.
 */
export const BRING_BACK_NOTHING_NEW_SENTENCE =
  "Nothing new in this fork since you last brought it back.";

/** Bounds of a Live hand-off's supporting dialogue (W-ead01b93); the clients keep the newest rows within them. */
export const LIVE_HANDOFF_DIALOGUE_MAX_ROWS = 32;
export const LIVE_HANDOFF_DIALOGUE_MAX_CHARS = 16_000;

/** One speaker's turn of the voice call before the hand-off: the person's earlier words or Cleo's. */
export const LiveHandoffDialogueRow = Schema.Struct({
  speaker: Schema.Literals(["person", "cleo"]),
  text: TrimmedNonEmptyString.check(Schema.isMaxLength(LIVE_HANDOFF_DIALOGUE_MAX_CHARS)),
});
export type LiveHandoffDialogueRow = typeof LiveHandoffDialogueRow.Type;

/**
 * W-ead01b93: a Send that hands a spoken request from a Live voice call (Pocket
 * or the Desk) to the conversation's model. The message text is the person's
 * new words ONLY, and that is all the Send attests as the person's message.
 * The call's earlier dialogue (Cleo's lines and the person's earlier words)
 * travels here, separately: the Desk gives it to the model as marked context
 * that is not the person's request (liveVoiceMessage.ts liveHandoffTaskInput),
 * and nothing that decides on the person's behalf ever reads it. An empty
 * dialogue is a hand-off whose call shared none ("spoken request only").
 */
export const LiveHandoff = Schema.Struct({
  supportingDialogue: Schema.Array(LiveHandoffDialogueRow).check(
    Schema.isMaxLength(LIVE_HANDOFF_DIALOGUE_MAX_ROWS),
    Schema.makeFilter((rows) =>
      rows.reduce((total, row) => total + row.text.length, 0) <= LIVE_HANDOFF_DIALOGUE_MAX_CHARS
        ? undefined
        : `The supporting dialogue may hold at most ${LIVE_HANDOFF_DIALOGUE_MAX_CHARS} characters.`,
    ),
  ),
  /**
   * How many earlier rows of the call did not fit the bounds and were left
   * out (the newest are kept). The Desk tells the model so. A newest row too
   * long on its own is kept shortened, beginning with "… ".
   */
  omittedEarlierRows: Schema.optional(NonNegativeInt),
});
export type LiveHandoff = typeof LiveHandoff.Type;

/**
 * W-ead01b93: what a Live delegation's saved message is, recorded once by the
 * Desk when the message is sent or steered (`thread.message-sent`), never
 * recomputed from its text. Only a Live delegation's message carries it.
 * - `person-words`: the text holds no line of Cleo's: the person's words (a
 *   hand-off that carried `liveHandoff`), or the person's words under the
 *   Desk's fixed correction preface (a correction marked `liveOrigin`).
 * - `legacy-combined`: sent by a client that predates the separate fields; its
 *   one text may hold Cleo's lines. It is never the person's request.
 * A Live delegation's message saved before this record existed has none, and
 * is never the person's request either.
 */
export const LiveMessageOrigin = Schema.Literals(["person-words", "legacy-combined"]);
export type LiveMessageOrigin = typeof LiveMessageOrigin.Type;

/**
 * W-89a78759 (PR #1708): that the person typed this message themselves, which the Desk asks the World to record with the
 * message (`agentContextResolutions.personAuthorship`, backend/convex/lib/personAuthorship.js). SERVER-STAMPED, never
 * client-sent: the socket stamps `desk-typed` only on a composer Send from the person's own Desk session
 * (orchestration/personAuthorship.ts), and only a Send that starts its own turn carries it on to the World. The World
 * checks its own side of the source before recording it. A later slice may add a kind for the person's own side of a
 * Live transcript from their own Pocket.
 */
export const PersonAuthorship = Schema.Struct({ kind: Schema.Literal("desk-typed") });
export type PersonAuthorship = typeof PersonAuthorship.Type;

export const ThreadTurnStartCommand = Schema.Struct({
  queueReference: Schema.optional(MessageQueueReference),
  queueDelivery: Schema.optional(ThreadTurnQueueDelivery),
  type: Schema.Literal("thread.turn.start"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    role: Schema.Literal("user"),
    text: Schema.String,
    attachments: Schema.Array(ChatAttachment),
  }),
  replyReaction: Schema.optional(MessageReactionReply),
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  bootstrap: Schema.optional(ThreadTurnStartBootstrap),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  bringBack: Schema.optional(ThreadBringBack),
  /** W-ead01b93: a Live spoken hand-off; its call's dialogue, never part of the attested message text. */
  liveHandoff: Schema.optional(LiveHandoff),
  /**
   * Server-stamped, never client-sent (the client command has no such field):
   * the World session of the bound door this Send came through, which the engine
   * authorizes an immediate bring-back against. A door's bring-back is never
   * queued, so no new queued message carries one.
   */
  doorSession: Schema.optional(
    Schema.Struct({ worldKey: Schema.String, worldSessionId: Schema.String }),
  ),
  /**
   * Server-stamped, never client-sent (the client command has no such field; the
   * normalizer drops one): the person typed this Send in their own Desk's
   * composer (orchestration/personAuthorship.ts deskTypedAuthorship).
   */
  personAuthorship: Schema.optional(PersonAuthorship),
  // The frozen per-message boundary (O15/F12). Optional so pre-boundary
  // history decodes; a governed World message carries it, and its absence on
  // a governed path is a declared gap until the Send wiring covers every
  // entry point — never inferred, never invented.
  contextResolution: Schema.optional(MessageContextResolution),
  createdAt: IsoDateTime,
});

/** A committed correction for an exact running execution; never starts a turn. */
export const ThreadTurnSteerCommand = Schema.Struct({
  queueReference: Schema.optional(MessageQueueReference),
  /**
   * W-ead01b93: a Live spoken correction from a client that keeps Cleo's lines
   * out of the person's message. Absent from an older client's correction,
   * whose text may hold them (recorded `legacy-combined`).
   */
  liveOrigin: Schema.optional(Schema.Literal("person-words")),
  type: Schema.Literal("thread.turn.steer"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    role: Schema.Literal("user"),
    text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64000)),
    attachments: Schema.Array(ChatAttachment).check(Schema.isMaxLength(0)),
  }),
  contextResolution: AgentContextResolution,
  expectedTurnId: TurnId,
  target: Schema.Struct({
    executionId: TrimmedNonEmptyString,
    resolutionDigest: Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/u)),
  }),
  createdAt: IsoDateTime,
});
export type ThreadTurnSteerCommand = typeof ThreadTurnSteerCommand.Type;

export const TurnCorrectionState = Schema.Literals([
  "recorded",
  "dispatching",
  "accepted",
  "refused",
  "unknown",
]);
export const TurnCorrectionObservation = Schema.Struct({
  eventId: EventId,
  itemId: ProviderItemId,
  providerThreadId: TrimmedNonEmptyString,
  observedAt: IsoDateTime,
  textDigest: Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/u)),
});
export const TurnCorrectionActivityPayload = Schema.Struct({
  messageId: MessageId,
  expectedTurnId: TurnId,
  target: ThreadTurnSteerCommand.fields.target,
  resolutionId: TrimmedNonEmptyString,
  resolutionDigest: Schema.String,
  fingerprint: Schema.String,
  state: TurnCorrectionState,
  observation: Schema.optional(TurnCorrectionObservation),
  attemptId: Schema.optional(TrimmedNonEmptyString),
  detail: Schema.optional(Schema.String.check(Schema.isMaxLength(512))),
});
export type TurnCorrectionActivityPayload = typeof TurnCorrectionActivityPayload.Type;

const ThreadTurnSteerStatusCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.steer.status"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  fingerprint: TrimmedNonEmptyString,
  expectedState: Schema.Literals(["recorded", "dispatching", "unknown", "accepted"]),
  state: Schema.Literals(["dispatching", "accepted", "refused", "unknown"]),
  attemptId: TrimmedNonEmptyString,
  observation: Schema.optional(TurnCorrectionObservation),
  detail: Schema.optional(Schema.String.check(Schema.isMaxLength(512))),
  createdAt: IsoDateTime,
});

const ClientThreadTurnStartCommand = Schema.Struct({
  queueReference: Schema.optional(MessageQueueReference),
  queueDelivery: Schema.optional(ThreadTurnQueueDelivery),
  type: Schema.Literal("thread.turn.start"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    role: Schema.Literal("user"),
    text: Schema.String,
    attachments: Schema.Array(UploadChatAttachment),
  }),
  replyReaction: Schema.optional(MessageReactionReply),
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  bootstrap: Schema.optional(ThreadTurnStartBootstrap),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  bringBack: Schema.optional(ThreadBringBack),
  liveHandoff: Schema.optional(LiveHandoff),
  /**
   * W-89a78759 (PR #1708): the composer's own word that `message.text` is exactly
   * what the person typed (no attached context, stand-in, card choice or plan
   * text). Only a claim: the server turns it into `personAuthorship` only for
   * the person's own Desk session (orchestration/personAuthorship.ts).
   */
  composerTyped: Schema.optional(Schema.Literal(true)),
  /**
   * The pressed-choice law (the Orchestrator, 9 Oct 2026): the Desk's word that `message.text` is a choice the person
   * pressed on this card of Cleo's. Only a claim: the server stamps `personAuthorship` only for the person's own Desk
   * session and only when the text is byte-identical to a label of that card, still waiting, within the short length
   * (orchestration/personAuthorship.ts, @cleo/shared/richReply pressedChoiceOffered).
   */
  pressedChoice: Schema.optional(
    Schema.Struct({
      assistantMessageId: Schema.String,
      replyIndex: Schema.Number,
      cardId: Schema.String,
    }),
  ),
  contextResolution: Schema.optional(MessageContextResolution),
  createdAt: IsoDateTime,
});

const ThreadTurnInterruptCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.interrupt"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

/**
 * W-282f8d97: who answered an approval card. SERVER-STAMPED, never a client's to say: the socket and the Desk's HTTP
 * dispatch stamp `desk-person` only for the person's own Desk session (orchestration/personAuthorship.ts
 * deskPersonAnswer); every other door (a bot or `cleo pair` session, Pocket, the phone, harness tools) sends none, and
 * the client schema below has no such field, so a stamp a client sent is dropped as it is read. Only a stamped answer
 * reaches a card the Desk's chokepoint shows (provider/deskRunCards.ts).
 *
 * W-88de7558: two more server stamps, each set only where the server itself knows the door: `owner-phone`, the
 * owner channel's in-process answer from his verified number (neucleos/owner-channel/http.ts), and `paired-device`,
 * a socket session the server recorded at pairing as a paired device (issuanceOrigin "paired-device"; Pocket among
 * them). A card the person decides (a World gate instance he decides) takes only a stamped answer
 * (orchestration/personAuthorship.ts approvalAnswerAdmitted); a chokepoint card still takes only `desk-person`.
 */
export const ApprovalAnsweredBy = Schema.Literals(["desk-person", "owner-phone", "paired-device"]);
export type ApprovalAnsweredBy = typeof ApprovalAnsweredBy.Type;

const ClientThreadApprovalRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.approval.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
  createdAt: IsoDateTime,
});

const ThreadApprovalRespondCommand = Schema.Struct({
  ...ClientThreadApprovalRespondCommand.fields,
  answeredBy: Schema.optional(ApprovalAnsweredBy),
});

const ThreadUserInputRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.user-input.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
  createdAt: IsoDateTime,
});

/**
 * UP1: a compaction request is a door, not a message. It never enters the
 * transcript as a user message and never widens the session's boundary; the
 * engine summarizes what it already holds. The request id is client-minted so
 * a retry is idempotent and the transcript divider keeps one identity from
 * "requested" through "completed" or "failed".
 */
const ThreadContextCompactCommand = Schema.Struct({
  type: Schema.Literal("thread.context.compact"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
  modelSelection: Schema.optional(ModelSelection),
  createdAt: IsoDateTime,
});
export type ThreadContextCompactCommand = typeof ThreadContextCompactCommand.Type;

/** W-2494afb1: while a turn waits for a model on this Mac to start, one
 * activity per turn carries what the start is doing; each step replaces it. */
export const LOCAL_MODEL_LOADING_ACTIVITY_KIND = "local-model.loading";

/**
 * The reactor's explicit record that a tracked turn start (see `tracked` on
 * the turn-start-requested payload) is no longer outstanding. Payload:
 * `{ messageId, outcome: "adopted" | "failed" | "stopped" }`. Hidden from
 * every transcript.
 */
export const TURN_START_RESOLVED_ACTIVITY_KIND = "turn.start.resolved";

/** Client command ids never start with this: the server's own commands do. */
export const SERVER_COMMAND_ID_PREFIX = "server:";
/**
 * The one command id that may resolve the turn start accepted at event
 * sequence `acceptedSequence`: deterministic, so a repeat is a no-op and a
 * client can never write it.
 */
export const turnStartResolveCommandId = (acceptedSequence: number) =>
  `${SERVER_COMMAND_ID_PREFIX}turn-start-resolved:${acceptedSequence}`;
export const localModelLoadingActivityId = (threadId: string, turnId: string) =>
  `local-model-loading:${threadId}:${turnId}`;

export const CONTEXT_COMPACTION_ACTIVITY_KIND = "context-compaction";
export const contextCompactionActivityId = (requestId: string) => `context-compaction:${requestId}`;
export const ContextCompactionState = Schema.Literals([
  "requested",
  "compacting",
  "completed",
  "failed",
]);
export type ContextCompactionState = typeof ContextCompactionState.Type;
/**
 * The reactor-owned compaction record. One activity id per request; the
 * projector replaces it in place as the state advances. Engine-reported
 * "compacted" activities (ingestion, no requestId) sit beside it.
 */
export const ContextCompactionActivityPayload = Schema.Struct({
  state: ContextCompactionState,
  requestId: TrimmedNonEmptyString,
  requestedAt: IsoDateTime,
  modelSelection: Schema.optional(ModelSelection),
  completedAt: Schema.optional(IsoDateTime),
  beforeTokens: Schema.optional(NonNegativeInt),
  afterTokens: Schema.optional(NonNegativeInt),
  detail: Schema.optional(Schema.String.check(Schema.isMaxLength(2000))),
});
export type ContextCompactionActivityPayload = typeof ContextCompactionActivityPayload.Type;

const decodeContextCompactionPayload = Schema.decodeUnknownOption(ContextCompactionActivityPayload);
/** Reactor-owned compaction records in request order; engine-raw entries are skipped. */
export function readContextCompactions(
  activities: ReadonlyArray<{ readonly kind: string; readonly payload: unknown }>,
): ContextCompactionActivityPayload[] {
  return activities
    .flatMap((activity) => {
      if (activity.kind !== CONTEXT_COMPACTION_ACTIVITY_KIND) return [];
      const decoded = decodeContextCompactionPayload(activity.payload);
      return decoded._tag === "Some" ? [decoded.value] : [];
    })
    .sort(
      (a, b) =>
        a.requestedAt.localeCompare(b.requestedAt) || a.requestId.localeCompare(b.requestId),
    );
}
/** The compaction that still holds the session, if any: sends queue behind it. */
export function activeContextCompaction(
  activities: ReadonlyArray<{ readonly kind: string; readonly payload: unknown }>,
): ContextCompactionActivityPayload | undefined {
  return readContextCompactions(activities).find(
    (item) => item.state === "requested" || item.state === "compacting",
  );
}
/** Queue identity for a Send that arrived while the context was compacting. */
export const compactionQueueId = (messageId: string) => `compaction:${messageId}`;

const ThreadSessionStopCommand = Schema.Struct({
  type: Schema.Literal("thread.session.stop"),
  commandId: CommandId,
  threadId: ThreadId,
  createdAt: IsoDateTime,
  // Settle-cleanup stops are conditional: the decider drops the stop if the
  // thread was re-engaged (unsettled, session starting/running, or a queued
  // turn start) between the settle and this command. Guarding in the decider
  // closes the race a post-settle snapshot read cannot: commands are decided
  // serially against the authoritative read model.
  onlyIfSettled: Schema.optional(Schema.Boolean),
});

const ThreadMessageReactionSetCommand = Schema.Struct({
  type: Schema.Literal("thread.message.reaction.set"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  emoji: MessageReactionEmoji,
  reacted: Schema.Boolean,
  createdAt: IsoDateTime,
});

export const ThreadHistoryEditMode = Schema.Literals([
  "delete-one",
  "delete-from",
  "compact-before",
]);
export type ThreadHistoryEditMode = typeof ThreadHistoryEditMode.Type;
/**
 * One provider transcript fragment as the Live session delivered it (LVC1):
 * the exact delta text and its interval on the session timeline. Display rows
 * are revisable groups over these; the fragments are the retained original.
 */
export const LiveVoiceTranscriptFragment = Schema.Struct({
  eventId: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(8_000)),
  startMs: NonNegativeInt,
  endMs: NonNegativeInt,
});
export type LiveVoiceTranscriptFragment = typeof LiveVoiceTranscriptFragment.Type;
/** A message that entered the thread as spoken Live dialogue, never through Send. */
export const LiveVoiceMessageOrigin = Schema.Struct({
  kind: Schema.Literal("live-voice"),
  requestKey: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(128))),
  fragments: Schema.optional(
    Schema.Array(LiveVoiceTranscriptFragment).check(Schema.isMaxLength(1_024)),
  ),
});
export type LiveVoiceMessageOrigin = typeof LiveVoiceMessageOrigin.Type;

const ThreadHistoryEditCommand = Schema.Struct({
  type: Schema.Literal("thread.history.edit"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  mode: ThreadHistoryEditMode,
  expectedDigest: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)),
  summary: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(100_000))),
  createdAt: IsoDateTime,
});

/**
 * LVC1: one display row of a Live call, grouped by the client from the
 * provider's transcript fragments. The row's identity is stable for the call;
 * a later sync with the same id and new text revises the row in place.
 */
export const ThreadVoiceTranscriptMessage = Schema.Struct({
  messageId: MessageId,
  role: Schema.Literals(["user", "assistant"]),
  text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(32_000)),
  createdAt: IsoDateTime,
  fragments: Schema.optional(
    Schema.Array(LiveVoiceTranscriptFragment).check(Schema.isMaxLength(1_024)),
  ),
});
export type ThreadVoiceTranscriptMessage = typeof ThreadVoiceTranscriptMessage.Type;
/**
 * Save both speakers' Live words into the conversation they were spoken in.
 * Saving speech never dispatches a task: the command appends or revises
 * ordinary messages with no turn and advances the history revision so the
 * next typed or spoken turn continues from them. Sending the same rows twice
 * has one effect.
 */
const ThreadVoiceTranscriptSyncCommand = Schema.Struct({
  type: Schema.Literal("thread.voice.transcript.sync"),
  commandId: CommandId,
  threadId: ThreadId,
  worldKey: Schema.optional(TrimmedNonEmptyString),
  sessionId: Schema.optional(TrimmedNonEmptyString),
  requestKey: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(128))),
  messages: Schema.Array(ThreadVoiceTranscriptMessage).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(64),
  ),
  createdAt: IsoDateTime,
});
export type ThreadVoiceTranscriptSyncCommand = typeof ThreadVoiceTranscriptSyncCommand.Type;

const DispatchableClientOrchestrationCommand = Schema.Union([
  ProjectCreateCommand,
  ProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ThreadSettleCommand,
  ThreadUnsettleCommand,
  ThreadSnoozeCommand,
  ThreadUnsnoozeCommand,
  ThreadPinCommand,
  ThreadUnpinCommand,
  ThreadPinReorderCommand,
  ThreadActiveReorderCommand,
  ThreadMetaUpdateCommand,
  ThreadContextMoveCommand,
  ThreadContextPrepareCommand,
  ThreadMessageReactionSetCommand,
  ThreadMessageQueueUpdateCommand,
  ThreadMessageQueueResumeCommand,
  ThreadUsageLimitUpdateCommand,
  ThreadHistoryEditCommand,
  ThreadVoiceTranscriptSyncCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ThreadTurnStartCommand,
  ThreadTurnSteerCommand,
  ThreadTurnInterruptCommand,
  ThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadSessionStopCommand,
  ThreadContextCompactCommand,
]);
export type DispatchableClientOrchestrationCommand =
  typeof DispatchableClientOrchestrationCommand.Type;

export const ClientOrchestrationCommand = Schema.Union([
  ProjectCreateCommand,
  ProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ThreadSettleCommand,
  ThreadUnsettleCommand,
  ThreadSnoozeCommand,
  ThreadUnsnoozeCommand,
  ThreadPinCommand,
  ThreadUnpinCommand,
  ThreadPinReorderCommand,
  ThreadActiveReorderCommand,
  ThreadMetaUpdateCommand,
  ThreadContextMoveCommand,
  ThreadContextPrepareCommand,
  ThreadMessageReactionSetCommand,
  ThreadMessageQueueUpdateCommand,
  ThreadMessageQueueResumeCommand,
  ThreadUsageLimitUpdateCommand,
  ThreadHistoryEditCommand,
  ThreadVoiceTranscriptSyncCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ClientThreadTurnStartCommand,
  ThreadTurnSteerCommand,
  ThreadTurnInterruptCommand,
  ClientThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadSessionStopCommand,
  ThreadContextCompactCommand,
]);
export type ClientOrchestrationCommand = typeof ClientOrchestrationCommand.Type;

const ThreadSessionSetCommand = Schema.Struct({
  type: Schema.Literal("thread.session.set"),
  commandId: CommandId,
  threadId: ThreadId,
  session: OrchestrationSession,
  createdAt: IsoDateTime,
});

const ThreadMessageAssistantDeltaCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.delta"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  delta: Schema.String,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadMessageAssistantCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadProposedPlanUpsertCommand = Schema.Struct({
  type: Schema.Literal("thread.proposed-plan.upsert"),
  commandId: CommandId,
  threadId: ThreadId,
  proposedPlan: OrchestrationProposedPlan,
  createdAt: IsoDateTime,
});

const ThreadTurnDiffCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.diff.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: TurnId,
  completedAt: IsoDateTime,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.optional(MessageId),
  checkpointTurnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

const ThreadActivityAppendCommand = Schema.Struct({
  type: Schema.Literal("thread.activity.append"),
  commandId: CommandId,
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
  createdAt: IsoDateTime,
});

const ThreadRevertCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.revert.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  /** W-79778d2b: the engine's own session still holds the undone turns; the next Send carries the kept ones. */
  conversationCarry: Schema.optional(Schema.Boolean),
  /** W-2af5e7f1: the messages the undo removes, as the projection's rows name them. */
  removedMessageIds: Schema.optional(Schema.Array(MessageId)),
  createdAt: IsoDateTime,
});

const ThreadTitleRegenerationCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.title.regeneration.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: CommandId,
  title: Schema.optional(TrimmedNonEmptyString),
});

// Server-only: the reactor that delivered a fork's (or a switch's) carried
// conversation to the engine records it. Not part of ClientOrchestrationCommand.
const ThreadConversationCarryDeliverCommand = Schema.Struct({
  type: Schema.Literal("thread.conversation-carry.deliver"),
  commandId: CommandId,
  threadId: ThreadId,
  handoffId: TrimmedNonEmptyString,
  turnId: TurnId,
  createdAt: IsoDateTime,
});

// Server-only (Can 54): the engine reported something about a native helper.
// The decider creates or updates the helper's own read-only thread from it,
// idempotently, so a replayed report changes nothing.
export const ThreadHelperRecordCommand = Schema.Struct({
  type: Schema.Literal("thread.helper.record"),
  commandId: CommandId,
  parentThreadId: ThreadId,
  helperId: TrimmedNonEmptyString,
  engine: TrimmedNonEmptyString,
  state: HelperStatus,
  title: Schema.optional(TrimmedNonEmptyString),
  ask: Schema.optional(TrimmedNonEmptyString),
  role: Schema.optional(TrimmedNonEmptyString),
  model: Schema.optional(TrimmedNonEmptyString),
  effort: Schema.optional(TrimmedNonEmptyString),
  step: Schema.optional(TrimmedNonEmptyString),
  result: Schema.optional(TrimmedNonEmptyString),
  error: Schema.optional(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});
export type ThreadHelperRecordCommand = typeof ThreadHelperRecordCommand.Type;

// Server-only: the reactor's single write that resolves one tracked turn start
// (identified by the sequence of its accepting event). A failed outcome also
// commits the person-visible `provider.turn.start.failed` in the same
// transaction. Not part of ClientOrchestrationCommand.
export const ThreadTurnStartResolveCommand = Schema.Struct({
  type: Schema.Literal("thread.turn-start.resolve"),
  commandId: CommandId,
  threadId: ThreadId,
  acceptedSequence: NonNegativeInt,
  messageId: MessageId,
  outcome: Schema.Literals(["adopted", "failed", "stopped"]),
  providerTurnId: Schema.optional(TurnId),
  stopSequence: Schema.optional(NonNegativeInt),
  failure: Schema.optional(
    Schema.Struct({
      detail: Schema.String,
      dispatchOutcome: Schema.optional(Schema.Literals(["refused", "unknown"])),
      provider: Schema.optional(
        Schema.Struct({ instanceId: Schema.String, driverKind: Schema.String }),
      ),
      // Can 21: a refused start of the Send that switched engines, whose engine
      // never started, returns the conversation to where it was: the engine
      // that Send switched to, the selection to give back, and the engine that
      // held the conversation (when the selection given back is another engine
      // still owed the conversation, that switch is recorded again).
      revert: Schema.optional(
        Schema.Struct({
          switchedTo: ProviderInstanceId,
          to: ModelSelection,
          from: Schema.optional(
            Schema.Struct({
              instanceId: ProviderInstanceId,
              model: Schema.NullOr(TrimmedNonEmptyString),
              driverKind: Schema.NullOr(ProviderDriverKind),
            }),
          ),
        }),
      ),
    }),
  ),
  createdAt: IsoDateTime,
});
export type ThreadTurnStartResolveCommand = typeof ThreadTurnStartResolveCommand.Type;

const InternalOrchestrationCommand = Schema.Union([
  ThreadTurnStartResolveCommand,
  ThreadHelperRecordCommand,
  ThreadMessageQueuePauseCommand,
  ThreadConversationCarryDeliverCommand,
  ThreadTurnSteerStatusCommand,
  ThreadSessionSetCommand,
  ThreadMessageAssistantDeltaCommand,
  ThreadMessageAssistantCompleteCommand,
  ThreadProposedPlanUpsertCommand,
  ThreadTurnDiffCompleteCommand,
  ThreadActivityAppendCommand,
  ThreadRevertCompleteCommand,
  ThreadTitleRegenerationCompleteCommand,
]);
export type InternalOrchestrationCommand = typeof InternalOrchestrationCommand.Type;

export const OrchestrationCommand = Schema.Union([
  DispatchableClientOrchestrationCommand,
  InternalOrchestrationCommand,
]);
export type OrchestrationCommand = typeof OrchestrationCommand.Type;

export const OrchestrationEventType = Schema.Literals([
  "project.created",
  "project.meta-updated",
  "project.deleted",
  "thread.created",
  "thread.history-inherited",
  "thread.conversation-carry-delivered",
  "thread.engine-switched",
  "thread.deleted",
  "thread.archived",
  "thread.unarchived",
  "thread.settled",
  "thread.unsettled",
  "thread.snoozed",
  "thread.unsnoozed",
  "thread.pinned",
  "thread.unpinned",
  "thread.pin-reordered",
  "thread.meta-updated",
  "thread.context-moved",
  "thread.context-prepared",
  "thread.runtime-mode-set",
  "thread.interaction-mode-set",
  "thread.message-sent",
  "thread.message-reaction-set",
  "thread.history-edited",
  "thread.message-reactions-delivered",
  "thread.turn-start-requested",
  "thread.turn-interrupt-requested",
  "thread.approval-response-requested",
  "thread.user-input-response-requested",
  "thread.checkpoint-revert-requested",
  "thread.reverted",
  "thread.session-stop-requested",
  "thread.session-set",
  "thread.proposed-plan-upserted",
  "thread.turn-diff-completed",
  "thread.activity-appended",
]);
export type OrchestrationEventType = typeof OrchestrationEventType.Type;

export const OrchestrationAggregateKind = Schema.Literals(["project", "thread"]);
export type OrchestrationAggregateKind = typeof OrchestrationAggregateKind.Type;
export const OrchestrationActorKind = Schema.Literals(["client", "server", "provider"]);

export const ProjectCreatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  // Optional so persisted events from older servers still decode.
  faviconPath: Schema.optional(Schema.NullOr(ProjectFaviconPath)),
  scripts: Schema.Array(ProjectScript),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ProjectMetaUpdatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  defaultThreadEnvMode: Schema.optional(Schema.NullOr(ThreadEnvMode)),
  faviconPath: Schema.optional(Schema.NullOr(ProjectFaviconPath)),
  scripts: Schema.optional(Schema.Array(ProjectScript)),
  updatedAt: IsoDateTime,
});

export const ProjectDeletedPayload = Schema.Struct({
  projectId: ProjectId,
  deletedAt: IsoDateTime,
});

export const ThreadCreatedPayload = withWorkroomReadCompatibility(
  Schema.Struct({
    threadId: ThreadId,
    projectId: ProjectId,
    // Optional so thread.created events from before World ownership still decode.
    worldKey: Schema.optional(TrimmedNonEmptyString),
    // Optional so historical and Local thread events remain decodable.
    sessionId: Schema.optional(TrimmedNonEmptyString),
    title: TrimmedNonEmptyString,
    // Optional so thread.created events from before Arc A3 launch intent decode.
    launchAsk: Schema.optional(TrimmedNonEmptyString),
    // Optional so thread.created events from before Arc A5 explicit parentage
    // decode; absent decodes to a root.
    parentThreadId: Schema.optional(ThreadId),
    // Fork (Can 46). The inherited entries follow in thread.history-inherited
    // events; inheritedOmitted counts the older entries the bound left out.
    forkedFrom: Schema.optional(
      Schema.Struct({ ...ThreadForkedFrom.fields, inheritedOmitted: NonNegativeInt }),
    ),
    // Can 54: this thread is a helper's read-only conversation.
    helper: Schema.optional(ThreadHelper),
    // Optional so thread.created events from before workrooms decode.
    workroomKey: Schema.optional(TrimmedNonEmptyString),
    localWorkPolicy: Schema.optional(LocalWorkPolicy),
    localSelection: Schema.optional(LocalContextSelection),
    modelSelection: ModelSelection,
    runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
    interactionMode: ProviderInteractionMode.pipe(
      Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
    ),
    branch: Schema.NullOr(TrimmedNonEmptyString),
    worktreePath: Schema.NullOr(TrimmedNonEmptyString),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  }),
);

export const ThreadHistoryInheritedPayload = Schema.Struct({
  threadId: ThreadId,
  entries: Schema.Array(InheritedMessage).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(FORK_INHERITED_EVENT_MAX_ENTRIES),
  ),
});
export type ThreadHistoryInheritedPayload = typeof ThreadHistoryInheritedPayload.Type;

export const ThreadConversationCarryDeliveredPayload = Schema.Struct({
  threadId: ThreadId,
  handoffId: TrimmedNonEmptyString,
  turnId: TurnId,
  deliveredAt: IsoDateTime,
});
export type ThreadConversationCarryDeliveredPayload =
  typeof ThreadConversationCarryDeliveredPayload.Type;

/**
 * Can 21: a Send in a started conversation named another engine. Recorded
 * before that Send's `thread.turn-start-requested`; the thread's selection
 * moves to `to` and the conversation so far is owed to it (an engine-switch
 * carry). `from` is the engine that holds the conversation: its model is null
 * when the thread's selection had already moved off it, and its driver kind
 * null when the registry no longer knows it. `reverted` marks a Send that
 * named the bound engine again before the engine switched to ever ran: the
 * selection returns to it and the owed carry is dropped. A refused start of
 * the switching Send records the same revert (its `from` the engine that never
 * started). `previous` is the thread's selection before the Send, absent on a
 * revert and on switches recorded before it existed.
 */
export const ThreadEngineSwitchedPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  from: Schema.Struct({
    instanceId: ProviderInstanceId,
    model: Schema.NullOr(TrimmedNonEmptyString),
    driverKind: Schema.NullOr(ProviderDriverKind),
  }),
  to: ModelSelection,
  previous: Schema.optional(ModelSelection),
  switchedAt: IsoDateTime,
  reverted: Schema.optional(Schema.Literal(true)),
});
export type ThreadEngineSwitchedPayload = typeof ThreadEngineSwitchedPayload.Type;

export const ThreadDeletedPayload = Schema.Struct({
  threadId: ThreadId,
  deletedAt: IsoDateTime,
});

export const ThreadArchivedPayload = Schema.Struct({
  threadId: ThreadId,
  archivedAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadUnarchivedPayload = Schema.Struct({
  threadId: ThreadId,
  updatedAt: IsoDateTime,
});

export const ThreadSettledPayload = Schema.Struct({
  threadId: ThreadId,
  settledAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadUnsettledPayload = Schema.Struct({
  threadId: ThreadId,
  reason: Schema.Literals(["user", "activity"]),
  updatedAt: IsoDateTime,
});

export const ThreadSnoozedPayload = Schema.Struct({
  threadId: ThreadId,
  snoozedUntil: IsoDateTime,
  snoozedAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadUnsnoozedPayload = Schema.Struct({
  threadId: ThreadId,
  // user: explicit "wake now". activity: real work arrived (user message /
  // session coming alive) and the decider cleared the snooze — mirrors
  // thread.unsettled's activity resets. Timer wakes emit no event: clients
  // derive them from snoozedUntil passing.
  reason: Schema.Literals(["user", "activity"]),
  updatedAt: IsoDateTime,
});

export const ThreadPinnedPayload = Schema.Struct({
  threadId: ThreadId,
  pinnedAt: IsoDateTime,
  // Absent on re-pins of an already-pinned thread (the existing key wins)
  // and on pins from clients that predate reordering.
  pinOrderKey: Schema.optional(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});

export const ThreadUnpinnedPayload = Schema.Struct({
  threadId: ThreadId,
  updatedAt: IsoDateTime,
});

export const ThreadPinReorderedPayload = Schema.Struct({
  threadId: ThreadId,
  orderKey: TrimmedNonEmptyString,
  updatedAt: IsoDateTime,
});

export const ThreadMetaUpdatedPayload = Schema.Struct({
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  /** Intent marker consumed by the title-generation reactor. Keeping this on
      the existing event lets older clients safely ignore the new field. */
  regenerateTitle: Schema.optional(Schema.Literal(true)),
  /** Title at request time, used to avoid overwriting a later manual rename. */
  previousTitle: Schema.optional(TrimmedNonEmptyString),
  /** Pending state shared with clients. Null clears a matching request. */
  titleRegeneration: Schema.optional(Schema.NullOr(ThreadTitleRegeneration)),
  modelSelection: Schema.optional(ModelSelection),
  branch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  /** Slot in the user-arranged active order; null clears it. */
  activeOrderKey: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  updatedAt: IsoDateTime,
});

export const ThreadContextMovedPayload = Schema.Struct({
  threadId: ThreadId,
  selection: LocalContextSelection,
  updatedAt: IsoDateTime,
});

export const ThreadRuntimeModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  runtimeMode: RuntimeMode,
  updatedAt: IsoDateTime,
});

export const ThreadInteractionModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  updatedAt: IsoDateTime,
});

// This descriptor is derived by the server from the original event stream,
// never from the summary draft or an incoming edit command.
export const ConversationHistoryOriginal = Schema.Struct({
  rootEventId: TrimmedNonEmptyString,
  throughSequence: NonNegativeInt,
  throughStreamVersion: NonNegativeInt,
  rangeDigest: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/u)),
  worldKey: Schema.optional(TrimmedNonEmptyString),
  memberId: Schema.optional(TrimmedNonEmptyString),
});
export type ConversationHistoryOriginal = typeof ConversationHistoryOriginal.Type;

export const ThreadHistoryEditedPayload = Schema.Struct({
  threadId: ThreadId,
  historyRevision: TrimmedNonEmptyString,
  removedMessageIds: Schema.Array(MessageId),
  removedTurnIds: Schema.Array(TurnId),
  summaryMessage: Schema.optional(OrchestrationMessage),
  original: Schema.optional(ConversationHistoryOriginal),
});
export type ThreadHistoryEditedPayload = typeof ThreadHistoryEditedPayload.Type;

export const ThreadMessageSentPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  reactions: Schema.optional(MessageReactionState),
  reactionContext: Schema.optional(Schema.Array(MessageReactionContext)),
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  contextResolution: Schema.optional(MessageContextResolution),
  historicalContextResolution: Schema.optional(HistoricalContextResolution),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  // LVC1: spoken dialogue saved into the thread. Absent for every ordinary
  // Send and provider reply; a reactor that treats a user message as a turn
  // request must skip it.
  origin: Schema.optional(LiveVoiceMessageOrigin),
  // W-ead01b93: a Live delegation's message records what it is, once, at
  // Send or steer. The person's request is read from this, never its text.
  liveOrigin: Schema.optional(LiveMessageOrigin),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadTurnStartRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  offlineContextReceipt: Schema.optional(OfflineContextReceipt),
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  // The frozen boundary rides the event so replay reads back the exact
  // resolution the message carried (O30); optional keeps history decodable.
  contextResolution: Schema.optional(MessageContextResolution),
  historicalContextResolution: Schema.optional(HistoricalContextResolution),
  // A Steer: the message joins the running turn named by its own `turnId`
  // instead of starting one. No pending turn opens for it; only the engine's
  // own completion ends the turn.
  midRun: Schema.optional(Schema.Literal(true)),
  // The engine's explicit record that it accepted this Send's turn start
  // ("turn.start.accepted", its message id being this event's). Its
  // resolution is recorded by the reactor as a `turn.start.resolved` activity
  // (TURN_START_RESOLVED_ACTIVITY_KIND): a start is outstanding from the
  // first until the second. Starts recorded before this field carry neither.
  tracked: Schema.optional(Schema.Literal(true)),
  // Can 46 (I10): the Send brings this fork back. Recorded on the event so a
  // restart or a retry reads the same intent; the merge-back record itself is
  // the reactor's `context.handoff` activity.
  bringBack: Schema.optional(ThreadBringBack),
  // W-ead01b93: a Live hand-off's supporting dialogue rides the event (the
  // message text is the person's words only), so a restart or a retry gives
  // the model the same marked context. Absent on every other Send.
  liveHandoff: Schema.optional(LiveHandoff),
  // W-89a78759 (PR #1708): the person typed this Send in their own Desk's
  // composer (server-stamped). Only a Send that starts its own turn, never a
  // queued, released or steered one, carries it.
  personAuthorship: Schema.optional(PersonAuthorship),
  createdAt: IsoDateTime,
});

export const ThreadTurnInterruptRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

export const ThreadApprovalResponseRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
  /** W-282f8d97, W-88de7558: the server's stamp of the door the person answered from (ApprovalAnsweredBy). */
  answeredBy: Schema.optional(ApprovalAnsweredBy),
  createdAt: IsoDateTime,
});

const ThreadUserInputResponseRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
  createdAt: IsoDateTime,
});

export const ThreadCheckpointRevertRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

export const ThreadRevertedPayload = Schema.Struct({
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  /** W-79778d2b: the next Send starts the engine fresh with the kept messages (a "revert" carry). */
  conversationCarry: Schema.optional(Schema.Boolean),
  /**
   * W-2af5e7f1: the messages this undo removes. Every copy of the conversation
   * drops exactly these; absent from older events, which keep the count rule.
   */
  removedMessageIds: Schema.optional(Schema.Array(MessageId)),
});

export const ThreadSessionStopRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

export const ThreadSessionSetPayload = Schema.Struct({
  threadId: ThreadId,
  session: OrchestrationSession,
});

export const ThreadProposedPlanUpsertedPayload = Schema.Struct({
  threadId: ThreadId,
  proposedPlan: OrchestrationProposedPlan,
});

export const ThreadTurnDiffCompletedPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});

export const ThreadActivityAppendedPayload = Schema.Struct({
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
});

export const OrchestrationEventMetadata = Schema.Struct({
  providerTurnId: Schema.optional(TrimmedNonEmptyString),
  providerItemId: Schema.optional(ProviderItemId),
  adapterKey: Schema.optional(TrimmedNonEmptyString),
  requestId: Schema.optional(ApprovalRequestId),
  ingestedAt: Schema.optional(IsoDateTime),
});
export type OrchestrationEventMetadata = typeof OrchestrationEventMetadata.Type;

const EventBaseFields = {
  sequence: NonNegativeInt,
  eventId: EventId,
  aggregateKind: OrchestrationAggregateKind,
  aggregateId: Schema.Union([ProjectId, ThreadId]),
  occurredAt: IsoDateTime,
  commandId: Schema.NullOr(CommandId),
  causationEventId: Schema.NullOr(EventId),
  correlationId: Schema.NullOr(CommandId),
  metadata: OrchestrationEventMetadata,
} as const;

export const OrchestrationEvent = Schema.Union([
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.history-edited"),
    payload: ThreadHistoryEditedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.created"),
    payload: ProjectCreatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.meta-updated"),
    payload: ProjectMetaUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.deleted"),
    payload: ProjectDeletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.created"),
    payload: ThreadCreatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.history-inherited"),
    payload: ThreadHistoryInheritedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.conversation-carry-delivered"),
    payload: ThreadConversationCarryDeliveredPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.engine-switched"),
    payload: ThreadEngineSwitchedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.deleted"),
    payload: ThreadDeletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.archived"),
    payload: ThreadArchivedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.unarchived"),
    payload: ThreadUnarchivedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.settled"),
    payload: ThreadSettledPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.unsettled"),
    payload: ThreadUnsettledPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.snoozed"),
    payload: ThreadSnoozedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.unsnoozed"),
    payload: ThreadUnsnoozedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pinned"),
    payload: ThreadPinnedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.unpinned"),
    payload: ThreadUnpinnedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pin-reordered"),
    payload: ThreadPinReorderedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.meta-updated"),
    payload: ThreadMetaUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.context-prepared"),
    payload: Schema.Struct({
      threadId: ThreadId,
      messageId: MessageId,
      resolution: LocalContextResolution,
      createdAt: IsoDateTime,
    }),
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.context-moved"),
    payload: ThreadContextMovedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.runtime-mode-set"),
    payload: ThreadRuntimeModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.interaction-mode-set"),
    payload: ThreadInteractionModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.message-reactions-delivered"),
    payload: Schema.Struct({ threadId: ThreadId, messageId: MessageId }),
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.message-reaction-set"),
    payload: Schema.Struct({
      threadId: ThreadId,
      messageId: MessageId,
      reactions: MessageReactionState,
    }),
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.message-sent"),
    payload: ThreadMessageSentPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-start-requested"),
    payload: ThreadTurnStartRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-interrupt-requested"),
    payload: ThreadTurnInterruptRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.approval-response-requested"),
    payload: ThreadApprovalResponseRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.user-input-response-requested"),
    payload: ThreadUserInputResponseRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.checkpoint-revert-requested"),
    payload: ThreadCheckpointRevertRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.reverted"),
    payload: ThreadRevertedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.session-stop-requested"),
    payload: ThreadSessionStopRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.session-set"),
    payload: ThreadSessionSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.proposed-plan-upserted"),
    payload: ThreadProposedPlanUpsertedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-diff-completed"),
    payload: ThreadTurnDiffCompletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.activity-appended"),
    payload: ThreadActivityAppendedPayload,
  }),
]);
export type OrchestrationEvent = typeof OrchestrationEvent.Type;

export const OrchestrationThreadStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("synchronized"),
  }),
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: OrchestrationThreadDetailSnapshot,
  }),
  Schema.Struct({
    kind: Schema.Literal("event"),
    event: OrchestrationEvent,
  }),
]);
export type OrchestrationThreadStreamItem = typeof OrchestrationThreadStreamItem.Type;

export const OrchestrationCommandReceiptStatus = Schema.Literals(["accepted", "rejected"]);
export type OrchestrationCommandReceiptStatus = typeof OrchestrationCommandReceiptStatus.Type;

export const TurnCountRange = Schema.Struct({
  fromTurnCount: NonNegativeInt,
  toTurnCount: NonNegativeInt,
}).check(
  Schema.makeFilter(
    (input) =>
      input.fromTurnCount <= input.toTurnCount ||
      new SchemaIssue.InvalidValue({
        message: "fromTurnCount must be less than or equal to toTurnCount",
      }),
    { identifier: "OrchestrationTurnDiffRange" },
  ),
);

export const ThreadTurnDiff = TurnCountRange.mapFields(
  Struct.assign({
    threadId: ThreadId,
    diff: Schema.String,
  }),
  { unsafePreserveChecks: true },
);

export const ProviderSessionRuntimeStatus = Schema.Literals([
  "starting",
  "running",
  "stopped",
  "error",
]);
export type ProviderSessionRuntimeStatus = typeof ProviderSessionRuntimeStatus.Type;

const ProjectionThreadTurnStatus = Schema.Literals([
  "running",
  "completed",
  "interrupted",
  "error",
]);
export type ProjectionThreadTurnStatus = typeof ProjectionThreadTurnStatus.Type;

const ProjectionCheckpointRow = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});
export type ProjectionCheckpointRow = typeof ProjectionCheckpointRow.Type;

export const ProjectionPendingApprovalStatus = Schema.Literals(["pending", "resolved"]);
export type ProjectionPendingApprovalStatus = typeof ProjectionPendingApprovalStatus.Type;

export const ProjectionPendingApprovalDecision = Schema.NullOr(ProviderApprovalDecision);
export type ProjectionPendingApprovalDecision = typeof ProjectionPendingApprovalDecision.Type;

export const DispatchResult = Schema.Struct({
  sequence: NonNegativeInt,
  /**
   * Can 46 (W-de479025): the conversation a bound door's fork birth created, or
   * found already saved by an earlier attempt. The server chooses it from the
   * door's World session and the birth's request key; the door opens this id.
   */
  threadId: Schema.optional(ThreadId),
});
export type DispatchResult = typeof DispatchResult.Type;

export const OrchestrationGetTurnDiffInput = TurnCountRange.mapFields(
  Struct.assign({
    threadId: ThreadId,
    ignoreWhitespace: Schema.optionalKey(Schema.Boolean),
  }),
  { unsafePreserveChecks: true },
);
export type OrchestrationGetTurnDiffInput = typeof OrchestrationGetTurnDiffInput.Type;

export const OrchestrationGetTurnDiffResult = ThreadTurnDiff;
export type OrchestrationGetTurnDiffResult = typeof OrchestrationGetTurnDiffResult.Type;

export const OrchestrationGetFullThreadDiffInput = Schema.Struct({
  threadId: ThreadId,
  toTurnCount: NonNegativeInt,
  ignoreWhitespace: Schema.optionalKey(Schema.Boolean),
});
export type OrchestrationGetFullThreadDiffInput = typeof OrchestrationGetFullThreadDiffInput.Type;

export const OrchestrationGetFullThreadDiffResult = ThreadTurnDiff;
export type OrchestrationGetFullThreadDiffResult = typeof OrchestrationGetFullThreadDiffResult.Type;

export const OrchestrationThreadSearchSource = Schema.Literals(["user", "assistant"]);
export type OrchestrationThreadSearchSource = typeof OrchestrationThreadSearchSource.Type;

// The server's SQLite client is synchronous and single-connection. Bound both
// scan input and response size so a search cannot monopolize that connection.
export const OrchestrationSearchThreadsInput = Schema.Struct({
  query: TrimmedString.check(Schema.isMinLength(2), Schema.isMaxLength(200)),
  limit: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 50 }))),
});
export type OrchestrationSearchThreadsInput = typeof OrchestrationSearchThreadsInput.Type;

export const OrchestrationThreadSearchMatch = Schema.Struct({
  threadId: ThreadId,
  projectId: ProjectId,
  source: OrchestrationThreadSearchSource,
  snippet: Schema.String.check(Schema.isMaxLength(240)),
  messageCreatedAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationThreadSearchMatch = typeof OrchestrationThreadSearchMatch.Type;

export const OrchestrationSearchThreadsResult = Schema.Struct({
  matches: Schema.Array(OrchestrationThreadSearchMatch),
});
export type OrchestrationSearchThreadsResult = typeof OrchestrationSearchThreadsResult.Type;

export const OrchestrationGetWorkflowScriptInput = Schema.Struct({
  threadId: ThreadId,
  /** Absolute path from the workflow's runHandles.scriptPath. The server
   * re-derives containment; the client value is a hint, never trusted. */
  scriptPath: TrimmedNonEmptyString,
});
export type OrchestrationGetWorkflowScriptInput = typeof OrchestrationGetWorkflowScriptInput.Type;

export const OrchestrationGetWorkflowScriptResult = Schema.Struct({
  scriptPath: TrimmedNonEmptyString,
  contents: Schema.String,
  truncated: Schema.Boolean,
});
export type OrchestrationGetWorkflowScriptResult = typeof OrchestrationGetWorkflowScriptResult.Type;

const WORKFLOW_SCRIPT_ERROR_MESSAGES = {
  "invalid-path": "Workflow scripts must be absolute .js paths.",
  "root-unavailable": "Script root unavailable.",
  "not-found": "Script not found.",
  "outside-root": "Script path is outside the workflow scripts root.",
  "not-js": "Resolved script is not a .js file.",
  "not-regular-file": "Script is not a regular file.",
  "changed-during-read": "Script changed between resolution and open.",
  "read-failed": "Script read failed.",
} as const;

export class OrchestrationGetWorkflowScriptError extends Schema.TaggedErrorClass<OrchestrationGetWorkflowScriptError>()(
  "OrchestrationGetWorkflowScriptError",
  {
    reason: Schema.Literals([
      "invalid-path",
      "root-unavailable",
      "not-found",
      "outside-root",
      "not-js",
      "not-regular-file",
      "changed-during-read",
      "read-failed",
    ]),
    scriptPath: Schema.String,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  override get message(): string {
    return WORKFLOW_SCRIPT_ERROR_MESSAGES[this.reason];
  }
}

export const OrchestrationRpcSchemas = {
  dispatchCommand: {
    input: ClientOrchestrationCommand,
    output: DispatchResult,
  },
  getWorkflowScript: {
    input: OrchestrationGetWorkflowScriptInput,
    output: OrchestrationGetWorkflowScriptResult,
  },
  getTurnDiff: {
    input: OrchestrationGetTurnDiffInput,
    output: OrchestrationGetTurnDiffResult,
  },
  getFullThreadDiff: {
    input: OrchestrationGetFullThreadDiffInput,
    output: OrchestrationGetFullThreadDiffResult,
  },
  searchThreads: {
    input: OrchestrationSearchThreadsInput,
    output: OrchestrationSearchThreadsResult,
  },
  getArchivedShellSnapshot: {
    input: Schema.Struct({}),
    output: OrchestrationShellSnapshot,
  },
  // W-d0329709: whether bringing this fork back would be refused as "nothing
  // new", read from the original's own record. False for a thread that is not a fork.
  getBringBackReadiness: {
    input: Schema.Struct({ forkThreadId: ThreadId }),
    output: Schema.Struct({ nothingNew: Schema.Boolean }),
  },
  subscribeThread: {
    input: OrchestrationSubscribeThreadInput,
    output: OrchestrationThreadStreamItem,
  },
  subscribeShell: {
    input: OrchestrationSubscribeShellInput,
    output: OrchestrationShellStreamItem,
  },
} as const;

export class OrchestrationGetSnapshotError extends Schema.TaggedErrorClass<OrchestrationGetSnapshotError>()(
  "OrchestrationGetSnapshotError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationDispatchCommandError extends Schema.TaggedErrorClass<OrchestrationDispatchCommandError>()(
  "OrchestrationDispatchCommandError",
  {
    message: TrimmedNonEmptyString,
    // Only a definite command refusal permits a fresh identity. Missing means
    // unknown: a transport or post-commit failure may have lost the receipt.
    commandOutcome: Schema.optional(Schema.Literal("rejected")),
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationGetTurnDiffError extends Schema.TaggedErrorClass<OrchestrationGetTurnDiffError>()(
  "OrchestrationGetTurnDiffError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationGetFullThreadDiffError extends Schema.TaggedErrorClass<OrchestrationGetFullThreadDiffError>()(
  "OrchestrationGetFullThreadDiffError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationSearchThreadsError extends Schema.TaggedErrorClass<OrchestrationSearchThreadsError>()(
  "OrchestrationSearchThreadsError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}
